import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Paper,
    Step,
    StepButton,
    StepLabel,
    Stepper,
    Typography
} from "@mui/material";
import {ThemeProvider} from "@mui/material/styles";
import {useCompactFormTheme} from "../Utils/compactFormTheme";
import {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router";
import {useLocation, useParams, useSearchParams} from "react-router-dom";
import {useSelector} from "react-redux";
import ReactJson from "react-json-view";
import {LoadingButton} from "@mui/lab";

import BasicStep from "./CampaignSteps/BasicStep";
import DateStep from "./CampaignSteps/DateStep";
import AudienceStep from "./CampaignSteps/AudienceStep";
import SummaryStep from "./CampaignSteps/SummaryStep";
import JsonViewer from "../Utils/JsonViewer";
import {useFindCampaignByIdQuery, useSaveCampaignMutation} from "../../core/reducers/campaign";
import {useFindTemplateByIdQuery, useSaveTemplateMutation} from "../../core/reducers/templates";
import {useGetAllCampaignTypesQuery} from "../../core/reducers/campaignType";
import {SEARCH_TYPES} from "../../core/reducers/typeFilter";
import {getColor, getEnvName, saveHistory} from "../Utils/CommonUtils";
import {getDomainAlias} from "../../constants";
import {MODE} from "./campaignMode";
import {resolvePublisherReturnTo} from "../Publisher/Campaign/CampaignFilterState";
import {
    STEPS,
    STEP_ORDER,
    addAudience,
    addParameter,
    applyBundle,
    applyDate,
    applyField,
    applyOfferType,
    applyScheduleField,
    applyScheduleTill,
    applyTillDays,
    applyTillDuration,
    buildSavePayload,
    cloneAudience,
    deleteAudience,
    deleteParameter,
    getNextBlockReason,
    isTohClanTournamentType,
    setParameterData,
    updateParameter,
    validateAudiences,
    validateBasic,
    validateDistribution,
} from "./campaignFormModel";

// Публичные экспорты сохранены — их используют AB-тесты, шаблоны, bulk update и CloneFromCampaign.
export {
    Campaign,
    CampaignTemplate,
    ParametersSet,
    VersionNumericParameterSet,
    isVersionNumericParameter
} from "./campaignFormModel";

export const getCurrentParam = (campaign, paramIndex, currentAudienceIndex) => {
    return campaign?.audiences[currentAudienceIndex].parameters[paramIndex]
}

export const getCurrentParamAllowedValues = (campaign, paramIndex, currentAudienceIndex, defaultParameters) => {
    let param = getCurrentParam(campaign, paramIndex, currentAudienceIndex);
    if (!param.parameterData.parameterName) return [];
    let paramObj = defaultParameters.filter(p => p.parameterName === param.parameterData.parameterName)
    if (paramObj.length == 0) return []
    return paramObj[0].allowedValues;
}

const ID_CHECK_DELAY = 300;
const STEP_ERROR_KEYS = {
    [STEPS.BASIC]: ['campaignIdRequired', 'offerType', 'priority', '(Clone)', 'regexp'],
    [STEPS.DISTRIBUTION]: ['distributionType', 'date', 'durationHour'],
};

/**
 * Конструктор кампании / шаблона. Режимы (props.mode): CREATE, UPDATE, CLONE, POLY (шаблон из кампании).
 * props.template — шаблон (другие эндпоинты, расписание, без проверки naming regexp).
 */
export default function CampaignForm(props) {
    const {mode, template} = props;
    const params = useParams();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const sourceMode = useSelector((state) => state.data.SourceMode);
    const env = getEnvName('env', sourceMode, searchParams);
    // компактные поля только внутри конструктора (AudienceStep в других диалогах не затрагивается)
    const formTheme = useCompactFormTheme();
    const domainAlias = getDomainAlias();

    const [activeStep, setActiveStep] = useState(0);
    const [campaign, setCampaign] = useState(props.campaign);
    const [errorFields, setErrorFields] = useState(() => new Set());
    const [campaignForSave, setCampaignForSave] = useState(null);
    const [resultDialog, setResultDialog] = useState(null); // 'success' | 'error' | null

    const step = STEP_ORDER[activeStep];
    const {data: types, status: typeStatus} = useGetAllCampaignTypesQuery();

    const [saveCampaign, saveCampaignResponse] = useSaveCampaignMutation();
    const [saveTemplate, saveTemplateResponse] = useSaveTemplateMutation();
    const saveResponse = template ? saveTemplateResponse : saveCampaignResponse;

    // ---- проверка уникальности id (с задержкой на ввод) ----
    const [idForCheck, setIdForCheck] = useState(props.campaign.campaignId);
    const idPending = idForCheck !== campaign.campaignId;
    useEffect(() => {
        const timer = setTimeout(() => setIdForCheck(campaign.campaignId), ID_CHECK_DELAY);
        return () => clearTimeout(timer);
    }, [campaign.campaignId]);
    // при первом рендере тоже ждём задержку, как раньше (кнопка в состоянии загрузки)
    const [mounted, setMounted] = useState(false);
    useEffect(() => {
        const timer = setTimeout(() => setMounted(true), ID_CHECK_DELAY);
        return () => clearTimeout(timer);
    }, []);
    const inputNotVerified = !mounted || idPending;

    const skipIdCheck = mode === MODE.UPDATE || !idForCheck;
    const campaignLookup = useFindCampaignByIdQuery({ev: env, id: idForCheck}, {skip: template || skipIdCheck});
    const templateLookup = useFindTemplateByIdQuery({ev: env, id: idForCheck}, {skip: !template || skipIdCheck});
    const idLookup = template ? templateLookup : campaignLookup;
    const idTaken = mode !== MODE.UPDATE && !!campaign.campaignId && idLookup.status !== 'pending' && idLookup.isSuccess && !idLookup.isError;

    const visibleErrors = useMemo(() => {
        const errors = new Set(errorFields);
        if (idTaken) errors.add('campaignId');
        return errors;
    }, [errorFields, idTaken]);

    const isTohClan = isTohClanTournamentType(campaign.offerType, types, domainAlias);

    // ---- тип кампании: длительности анонса/экстра-времени и naming regexp при клонировании ----
    const typeParameters = useMemo(() => {
        const type = types?.[campaign.offerType];
        return {
            announceDuration: type?.announceDuration,
            extraDuration: type?.extraDuration,
            recommendedDuration: type?.recommendedDuration,
        };
    }, [types, campaign.offerType]);

    useEffect(() => {
        if (mode === MODE.CLONE && types && campaign.offerType) {
            setCampaign(current => ({...current, regexp: types[current.offerType]?.regexp}));
        }
    }, [typeStatus, campaign.offerType]); // eslint-disable-line react-hooks/exhaustive-deps

    // ---- тип и бандл из URL (?type=&bundleId=) имеют приоритет ----
    const urlType = searchParams.get('type');
    const urlBundle = searchParams.get('bundleId');
    useEffect(() => {
        setCampaign(current => {
            let next = current;
            if (urlBundle !== null && current.bundleId != urlBundle) next = {...next, bundleId: urlBundle};
            if (urlType !== null && current.offerType != urlType) next = {...next, offerType: urlType};
            return next;
        });
    }, [campaign.bundleId, campaign.offerType, urlType, urlBundle]);

    // ---- история редактирования ----
    useEffect(() => {
        if (mode === MODE.UPDATE) saveHistory(props.campaign.campaignId, env, SEARCH_TYPES.CAMPAIGNS);
    }, [saveResponse.status]); // eslint-disable-line react-hooks/exhaustive-deps

    // ---- результат сохранения ----
    useEffect(() => {
        if (saveResponse.status === 'fulfilled') setResultDialog('success');
        else if (saveResponse.isError) setResultDialog('error');
    }, [saveResponse.status, saveResponse.isError]);

    // ---- аудитории: живая валидация ----
    const audienceValidation = useMemo(
        () => validateAudiences(campaign.audiences, {template}),
        [campaign.audiences, template]
    );
    // как и раньше, ошибки аудиторий подсвечиваются сразу на шаге audience (у шаблонов — кроме нетронутых строк)
    const showAudienceErrors = step === STEPS.AUDIENCE;
    const audiencesForView = showAudienceErrors ? audienceValidation.audiences : campaign.audiences;
    const campaignForView = useMemo(
        () => audiencesForView === campaign.audiences ? campaign : {...campaign, audiences: audiencesForView},
        [campaign, audiencesForView]
    );
    const audienceErrorFields = useMemo(() => {
        if (!showAudienceErrors) return visibleErrors;
        return new Set([...visibleErrors, ...audienceValidation.rangeErrors]);
    }, [showAudienceErrors, visibleErrors, audienceValidation.rangeErrors]);

    const setErrors = useCallback((keys, active) => setErrorFields(current => {
        const next = new Set(current);
        keys.forEach(key => active.has(key) ? next.add(key) : next.delete(key));
        return next;
    }), []);

    // ---- обработчики полей ----
    const update = useCallback(updater => setCampaign(current => updater(current)), []);

    const handleChange = (event) => {
        const {name, value} = event.target;
        if (name === 'priority') setErrors(['priority'], new Set(/^[0-9\b]+$/.test(value) ? [] : ['priority']));
        if (name === 'distributionType' && (value === 'fixed' || value === 'permanent')) setErrors(['durationHour'], new Set());
        update(current => applyField(current, name, value));
    };
    const handleOfferTypeChange = (value, campaignTypes) => update(current => applyOfferType(current, value, {types: campaignTypes, domainAlias}));
    const handleBundleChange = (value) => update(current => applyBundle(current, value, {mode, types, domainAlias}));
    const handleBundleManualInputChange = (e) => update(current => ({...current, bundleId: e.target.value}));
    const handleMatchmakingTypeChange = (event, value) => {
        setErrors(['matchmakingType'], new Set(value ? [] : ['matchmakingType']));
        update(current => ({...current, matchmakingType: value}));
    };
    const handleTimeZoneChange = (value) => update(current => ({...current, timeZone: value}));
    const handleDateChange = (date, field) => {
        try {
            update(current => applyDate(current, field, date, {types}));
        } catch (e) {
            // неполная дата в пикере — игнорируем, как и раньше
        }
    };
    const handleTillDurationChange = (days) => update(current => applyTillDays(current, days));
    const handleRecommendedDurationChange = (seconds) => update(current => applyTillDuration(current, seconds));
    const handleChangeSchedule = (event) => update(current => applyScheduleField(current, event.target.name, event.target.value));
    const handleChangeScheduleTill = (date) => update(current => applyScheduleTill(current, date));

    const audienceUpdate = update;
    // стабильные обработчики: строки аудиторий мемоизированы
    const audienceHandlers = useMemo(() => ({
        handleAddNewAudience: () => audienceUpdate(addAudience),
        handleCloneAudience: (index) => audienceUpdate(current => cloneAudience(current, index)),
        handleDeleteAudience: (index) => audienceUpdate(current => deleteAudience(current, index)),
        handleAddNewParams: (audienceIndex) => audienceUpdate(current => addParameter(current, audienceIndex)),
        handleDeleteParams: (audienceIndex, paramIndex) => audienceUpdate(current => deleteParameter(current, audienceIndex, paramIndex)),
        handleParameterDataChange: (available, value, paramIndex, audienceIndex) =>
            audienceUpdate(current => setParameterData(current, available, value, audienceIndex, paramIndex)),
        handleParameterOperatorChange: (value, paramIndex, audienceIndex) =>
            audienceUpdate(current => updateParameter(current, audienceIndex, paramIndex, {operator: value})),
        handleParameterValueChange: (value, paramIndex, audienceIndex) =>
            audienceUpdate(current => updateParameter(current, audienceIndex, paramIndex, {paramValue: value})),
        handleManualInput: (e, paramIndex, audienceIndex) =>
            audienceUpdate(current => updateParameter(current, audienceIndex, paramIndex, {paramValue: e.target.value})),
        handleManualInputForOffset: (e, fieldName, paramIndex, audienceIndex) =>
            audienceUpdate(current => updateParameter(current, audienceIndex, paramIndex, {[fieldName]: e.target.value})),
        handleVersionNumericParameterChange: (fieldName, value, paramIndex, audienceIndex) =>
            audienceUpdate(current => updateParameter(current, audienceIndex, paramIndex, {[fieldName]: value})),
    }), [audienceUpdate]);
    // AudienceStep меняет fickle/repeatable через setCurrentCampaign(updater)
    const setCampaignFromStep = setCampaign;

    // ---- навигация по шагам ----
    const validateStep = () => {
        if (step === STEPS.BASIC) {
            const errors = validateBasic(campaign, {template});
            setErrors(STEP_ERROR_KEYS[STEPS.BASIC], errors);
            return errors.size === 0 && !idTaken && !errorFields.has('matchmakingType');
        }
        if (step === STEPS.DISTRIBUTION) {
            const errors = validateDistribution(campaign);
            setErrors(STEP_ERROR_KEYS[STEPS.DISTRIBUTION], errors);
            return errors.size === 0;
        }
        if (step === STEPS.AUDIENCE) return !audienceValidation.hasErrors;
        return true;
    };

    const handleNext = () => {
        if (!validateStep()) return;
        if (step === STEPS.BASIC && !campaign.bundleId) {
            // как и раньше: пустой bundle подставляется из id
            setCampaign(current => ({...current, bundleId: current.campaignId}));
        }
        if (step === STEPS.SUMMARY) {
            setCampaignForSave(buildSavePayload(campaign, {template, isTohClan}));
        } else {
            setActiveStep(activeStep + 1);
        }
    };

    const handleBack = () => setActiveStep(activeStep - 1);

    const handleSave = () => {
        const saveParams = {ev: env, body: campaignForSave};
        if (searchParams.get('clone') !== null) saveParams.original = params.id;
        if (template) saveTemplate(saveParams);
        else saveCampaign(saveParams);
        setCampaignForSave(null);
    };

    const handleCreateAnother = () => {
        setResultDialog(null);
        navigate(`/creator/${template ? 'templates' : 'campaigns'}?env=${env}`, {state: location.state});
    };

    const handleGoToPublisher = () => {
        const publisher = template ? 'templates' : 'campaigns';
        const fallback = `/publisher/${publisher}?env=${sourceMode.env1Name}&env2=${sourceMode.env2Name}`;
        navigate(resolvePublisherReturnTo(location.state, fallback));
    };

    // ---- кнопка Next ----
    const blockReason = getNextBlockReason({
        campaign,
        mode,
        step,
        types,
        idCheck: {pending: inputNotVerified || idLookup.isLoading, isFree: idLookup.isError},
        audienceHasErrors: audienceValidation.hasErrors,
    });
    const isSaving = !!saveResponse.isLoading;
    const nextLoading = isSaving || inputNotVerified || idLookup.isLoading;

    function getStepContent() {
        switch (step) {
            case STEPS.BASIC:
                return <BasicStep
                    template={template}
                    mode={mode}
                    types={types}
                    status={typeStatus}
                    currentCampaign={campaign}
                    setCurrentCampaign={setCampaign}
                    handleChange={handleChange}
                    handleInput={() => {}}
                    handleBundleChange={handleBundleChange}
                    handleOfferTypeChange={handleOfferTypeChange}
                    handleMatchmakingTypeChange={handleMatchmakingTypeChange}
                    showPreviousCampaignId={isTohClan}
                    handleBundleManualInputChange={handleBundleManualInputChange}
                    errorFields={visibleErrors}
                />;
            case STEPS.DISTRIBUTION:
                return <DateStep
                    currentCampaign={campaign}
                    errorFields={visibleErrors}
                    handleChange={handleChange}
                    typeParameters={typeParameters}
                    handleDateChange={handleDateChange}
                    handleTillDurationChange={handleTillDurationChange}
                    handleRecommendedDurationChange={handleRecommendedDurationChange}
                    handleTimeZoneChange={handleTimeZoneChange}
                    template={template}
                    handleChangeScheduleTill={handleChangeScheduleTill}
                    handleChangeSchedule={handleChangeSchedule}
                />;
            case STEPS.AUDIENCE:
                return <AudienceStep
                    errorAudienceIndexes={showAudienceErrors ? audienceValidation.errorAudienceIndexes : []}
                    currentCampaign={campaignForView}
                    setCurrentCampaign={setCampaignFromStep}
                    errorFields={audienceErrorFields}
                    handleChange={handleChange}
                    setErrorFields={() => {}}
                    {...audienceHandlers}
                    isTemplate={template}
                />;
            case STEPS.SUMMARY:
                return <SummaryStep
                    isTemplate={template}
                    currentCampaign={campaign}
                    typeParameters={typeParameters}
                />;
            default:
                throw new Error('Unknown step');
        }
    }

    const typeTitle = template ? 'TEMPLATE' : 'CAMPAIGN';
    const modeTitle = template && mode === MODE.POLY ? ' FROM ' : mode.toUpperCase();
    const backToVariants = !!searchParams.get('mode');
    const errorText = saveResponse?.error?.data?.error || saveResponse?.error?.data?.message;

    return (
        <ThemeProvider theme={formTheme}>
        <Box display={'flex'} justifyContent={'center'}>
            <Box component="main" maxWidth="md" width="100%" sx={{mb: 4}}>
                <Paper variant="outlined" sx={{my: 2, p: {xs: 2, md: 3}}}>
                    <Typography component="h1" variant="h5" align="center">
                        <span style={{color: getColor(mode)}}>
                            {template ? 'TEMPLATE ' : ''}{modeTitle} {mode === MODE.CREATE ? ' ' + typeTitle : ''}
                        </span> {props.title ? props.title : ''}
                    </Typography>

                    <Stepper nonLinear activeStep={activeStep} sx={{pt: 2, pb: 3}}>
                        {STEP_ORDER.map((label, index) => (
                            <Step key={label} completed={index < activeStep}>
                                {index < activeStep
                                    // к пройденным шагам можно вернуться кликом
                                    ? <StepButton onClick={() => setActiveStep(index)}>{label}</StepButton>
                                    : <StepLabel>{label}</StepLabel>}
                            </Step>
                        ))}
                    </Stepper>

                    {getStepContent()}

                    <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mt: 3}}>
                        <Box sx={{display: 'flex', gap: 1}}>
                            {activeStep !== 0 && <Button onClick={handleBack}>Back</Button>}
                            {activeStep === 0 && (backToVariants
                                ? <Button color={"warning"} variant={"contained"} onClick={() => navigate(`/variants`)}>Back</Button>
                                : <Button color={"warning"} variant={"contained"} onClick={handleGoToPublisher}>To Publisher</Button>)}
                            {!template && activeStep === 0 && props.title &&
                                <Button
                                    color={"error"}
                                    variant={"contained"}
                                    onClick={() => navigate(`/creator/templates/${props.title}?campaign&env=${env}`)}>
                                    Create template
                                </Button>}
                        </Box>

                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                            {blockReason && !nextLoading &&
                                <Typography variant="body2" color="text.secondary" className="campaign-form__block-reason">
                                    {blockReason}
                                </Typography>}
                            <LoadingButton
                                loading={nextLoading}
                                variant="contained"
                                onClick={handleNext}
                                disabled={!!blockReason}
                            >
                                {step === STEPS.SUMMARY ? 'SAVE' : 'NEXT'}
                            </LoadingButton>
                        </Box>
                    </Box>

                    <Dialog open={resultDialog === 'success'} onClose={() => setResultDialog(null)} scroll={"paper"}>
                        <DialogTitle>{template ? "Template saved" : "Campaign saved"}</DialogTitle>
                        <DialogContent dividers={true}>
                            <DialogContentText component="div">
                                <JsonViewer json={saveResponse.data}></JsonViewer>
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button variant="outlined" onClick={handleCreateAnother}>
                                Create another {template ? 'template' : 'campaign'}
                            </Button>
                            <Button variant="contained" onClick={handleGoToPublisher}>
                                Go to publisher
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Dialog open={resultDialog === 'error'} onClose={() => setResultDialog(null)}>
                        <Box minWidth={300} minHeight={200}>
                            <DialogTitle>Error</DialogTitle>
                            <DialogContent>
                                <DialogContentText>{errorText}</DialogContentText>
                                <DialogActions>
                                    <Button onClick={() => setResultDialog(null)}>Close</Button>
                                </DialogActions>
                            </DialogContent>
                        </Box>
                    </Dialog>

                    <Dialog scroll={"paper"} open={!!campaignForSave} onClose={() => setCampaignForSave(null)}>
                        <DialogTitle>{`Going to save ${template ? 'template' : 'campaign'} ${campaignForSave?.id}`}</DialogTitle>
                        <DialogContent dividers={true}>
                            {campaignForSave && <ReactJson
                                theme={'bright:inverted'}
                                src={campaignForSave}
                                iconStyle={'square'}
                                displayDataTypes={false}
                                displayObjectSize={false}
                                enableClipboard={false}
                            />}
                        </DialogContent>
                        <DialogActions>
                            <Button variant="outlined" onClick={() => setCampaignForSave(null)}>Cancel</Button>
                            <Button variant="contained" onClick={handleSave}>Save</Button>
                        </DialogActions>
                    </Dialog>
                </Paper>
            </Box>
        </Box>
        </ThemeProvider>
    );
}
